"use client"

import type React from "react"

import { useState } from "react"
import { Plus, Minus, Check, Trash2 } from "lucide-react"
import { useApp } from "@/lib/context"
import type { Dish } from "@/lib/api"
import { motion } from "framer-motion"

interface AddToDraftButtonProps {
  dish: Dish
  onClick: (e: React.MouseEvent) => void
  showRemove?: boolean
  size?: "sm" | "md" | "lg"
}

export default function AddToDraftButton({ dish, onClick, showRemove = true, size = "md" }: AddToDraftButtonProps) {
  const { t, draftItems, removeDraftItem, updateDraftItemQuantity } = useApp()
  const [isAdded, setIsAdded] = useState(false)

  const draftItem = draftItems.find((item) => item.dish._id === dish._id)
  const isInDraft = !!draftItem
  const quantity = draftItem?.quantity || 0

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onClick(e)
    setIsAdded(true)

    // Reset after animation - reduzido para 500ms
    setTimeout(() => {
      setIsAdded(false)
    }, 500)
  }

  const handleRemove = (e: React.MouseEvent) => {
    e.stopPropagation()
    removeDraftItem(dish._id)
  }

  const handleDecrease = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (draftItem && draftItem.quantity > 1) {
      updateDraftItemQuantity(dish._id, draftItem.quantity - 1)
    } else {
      removeDraftItem(dish._id)
    }
  }

  // Tamanhos dos botões baseados na prop size
  const buttonSizes = {
    sm: "p-1",
    md: "p-1.5",
    lg: "p-2",
  }

  const iconSizes = {
    sm: "w-3 h-3",
    md: "w-4 h-4",
    lg: "w-5 h-5",
  }

  const quantitySizes = {
    sm: "text-xs min-w-[16px]",
    md: "text-sm min-w-[20px]",
    lg: "text-base min-w-[24px]",
  }

  const buttonSize = buttonSizes[size]
  const iconSize = iconSizes[size]
  const quantitySize = quantitySizes[size]

  return (
    <div className="flex items-center gap-1">
      {isInDraft && showRemove && (
        <motion.button
          onClick={handleRemove}
          whileTap={{ scale: 0.9, transition: { duration: 0.1 } }}
          className={`${buttonSize} rounded-full bg-red-100 text-red-600 border border-red-300 hover:bg-red-200 transition-colors`}
          aria-label={t("removeItem")}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, transition: { duration: 0.2 } }}
          title={t("removeItem")}
        >
          <Trash2 className={iconSize} />
        </motion.button>
      )}

      {/* Botão de diminuir quantidade */}
      {isInDraft && (
        <motion.button
          onClick={handleDecrease}
          whileTap={{ scale: 0.9, transition: { duration: 0.1 } }}
          className={`${buttonSize} rounded-full bg-muted text-foreground border border-border hover:bg-muted/80 transition-colors`}
          aria-label={t("decreaseQuantity") || "Diminuir quantidade"}
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1, transition: { duration: 0.2 } }}
          title={t("decreaseQuantity") || "Diminuir quantidade"}
        >
          <Minus className={iconSize} />
        </motion.button>
      )}

      {/* Quantidade */}
      {isInDraft && (
        <span className={`font-medium bg-muted px-2 py-0.5 rounded-full text-center ${quantitySize}`}>
          {quantity}
        </span>
      )}

      {/* Botão de adicionar */}
      <motion.button
        onClick={handleClick}
        whileTap={{ scale: 0.9, transition: { duration: 0.1 } }}
        className={`${buttonSize} rounded-full transition-colors flex items-center justify-center ${
          isAdded
            ? "bg-green-500 text-white"
            : isInDraft
              ? "bg-green-100 text-green-600 border border-green-300 hover:bg-green-200"
              : "bg-primary text-primary-foreground hover:opacity-90"
        }`}
        aria-label={isInDraft ? t("addMore") : t("addToDraft")}
        title={isInDraft ? t("addMore") : t("addToDraft")}
      >
        {isAdded ? <Check className={iconSize} /> : <Plus className={iconSize} />}
      </motion.button>
    </div>
  )
}
